import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { uploadForRequest, markRequestRead } from '../../services/notificationService';

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700',
  uploaded: 'bg-green-100 text-green-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

export default function NotificationPanel({ requests, onRefresh, onClose }) {
  const { fbUser } = useAuth();
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);
  const [activeRequestId, setActiveRequestId] = useState(null);

  const handleUploadClick = async (requestId) => {
    // Mark as read when interacting
    try {
      await markRequestRead(fbUser, requestId);
    } catch (e) { /* ignore */ }
    setActiveRequestId(requestId);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeRequestId) return;

    setUploading(activeRequestId);
    try {
      await uploadForRequest(fbUser, activeRequestId, file);
      if (onRefresh) onRefresh();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(null);
      setActiveRequestId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 max-h-[70vh] flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Document Requests</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* List */}
      <div className="overflow-y-auto flex-1">
        {requests.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">
            No document requests yet
          </div>
        ) : (
          requests.map((req) => (
            <div
              key={req._id}
              className={`px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition ${
                !req.isRead && req.status === 'pending' ? 'bg-blue-50/50' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-slate-800 truncate">
                      {req.documentName}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_STYLES[req.status]}`}>
                      {req.status}
                    </span>
                  </div>
                  {req.message && (
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{req.message}</p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    Requested by {req.requestedByName} &middot; {formatTime(req.createdAt)}
                  </p>
                </div>

                {req.status === 'pending' && (
                  <button
                    onClick={() => handleUploadClick(req._id)}
                    disabled={uploading === req._id}
                    className="shrink-0 px-3 py-1.5 bg-brand-600 text-white text-xs font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 transition"
                  >
                    {uploading === req._id ? (
                      <span className="flex items-center gap-1">
                        <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Uploading
                      </span>
                    ) : 'Upload'}
                  </button>
                )}

                {req.status === 'uploaded' && (
                  <span className="shrink-0 text-green-600">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={handleFileSelect}
      />
    </div>
  );
}
